/**
 * Per-route SEO metadata for public pages (SPA-safe).
 *
 * `usePageMeta` applies a unique <title>, meta description and canonical
 * link while the page is mounted, and restores the previous values on
 * unmount so the rest of the app keeps its defaults. Googlebot executes
 * JS, so runtime tags are honoured for indexing.
 *
 * Fixes the Vrinsoft audit findings (6 Oct 2026):
 *   - duplicate titles/descriptions across /, /login, /signup and legal pages
 *   - missing canonical tags on those routes
 */
import { useEffect } from 'react';

const SITE_ORIGIN = 'https://rotahq.uk';

export function usePageMeta({ title, description, canonicalPath }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const metaDescription = document.querySelector('meta[name="description"]');
    const prevDescription = metaDescription?.getAttribute('content');
    if (description) metaDescription?.setAttribute('content', description);

    let canonical = null;
    if (canonicalPath) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = `${SITE_ORIGIN}${canonicalPath}`;
      canonical.setAttribute('data-page-seo', '1');
      document.head.appendChild(canonical);
    }

    return () => {
      document.title = prevTitle;
      if (description && prevDescription) {
        metaDescription?.setAttribute('content', prevDescription);
      }
      canonical?.remove();
    };
  }, [title, description, canonicalPath]);
}
